import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { APIError, MatterStatuteOfLimitations } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { syncSingleContactConnection } from "@/lib/utils/connections/item-contact-connections";
import {
  getStatuteDefendantConnection,
  STATUTE_DEFENDANT_CONNECTION_TYPE,
} from "@/lib/utils/connections/statute-contact-connections";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

export const useSyncStatuteDefendantConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      matterId,
      currentStatute,
      desiredContactId,
      statuteId,
    }: {
      currentStatute?: MatterStatuteOfLimitations;
      desiredContactId?: string;
      matterId: string;
      statuteId: string;
    }) =>
      syncSingleContactConnection({
        matterId,
        currentConnections: currentStatute?.connections,
        connectionType: STATUTE_DEFENDANT_CONNECTION_TYPE,
        current: getStatuteDefendantConnection(currentStatute),
        desiredContactId,
        sourceObjectId: statuteId,
        sourceObjectType: "matter_statute_of_limitations",
      }),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating statute defendant");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
  });
};
